"use client";

/**
 * Realtime hook for the customer-facing support widget.
 *
 * Mirrors `messages-context.tsx`'s subscription patterns (visibility-aware
 * exponential backoff, online/offline reconnect) but scoped to a single
 * `support_conversations` row. Subscribes only when there's a known
 * conversation id — anonymous/no-conversation users skip the WS entirely.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import {
  getMyActiveConversation,
  getSupportMessages,
  markSupportConversationAsRead,
} from "@/app/actions/support";
import type { SupportConversation, SupportMessage } from "@/types/support";

interface UseSupportConversationOptions {
  /** Whether the user is currently signed in. The hook short-circuits when false. */
  authed: boolean;
  /** Whether the panel is open. When false we skip realtime subscription. */
  active: boolean;
}

interface UseSupportConversationResult {
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  loading: boolean;
  unreadCount: number;
  markAsRead: () => void;
  refresh: () => Promise<void>;
  setConversation: (conv: SupportConversation | null) => void;
  appendMessage: (msg: SupportMessage) => void;
}

export function useSupportConversation(
  options: UseSupportConversationOptions,
): UseSupportConversationResult {
  const { authed, active } = options;
  const supabase = useMemo(() => createClient(), []);

  const [conversation, setConversation] = useState<SupportConversation | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conversation?.id ?? null;

  const refresh = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const convRes = await getMyActiveConversation();
      const conv = convRes.data;
      setConversation(conv);
      if (conv) {
        const msgRes = await getSupportMessages(conv.id, 50);
        setMessages(msgRes.data ?? []);
      } else {
        setMessages([]);
      }
    } finally {
      setLoading(false);
    }
  }, [authed]);

  // Initial load (and whenever auth flips on)
  useEffect(() => {
    if (!authed) {
      setConversation(null);
      setMessages([]);
      return;
    }
    void refresh();
  }, [authed, refresh]);

  // Realtime subscription, lazy: only when authed + panel active + we have a conversation
  useEffect(() => {
    if (!authed || !active) return;
    const conversationId = conversation?.id;
    if (!conversationId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let disposed = false;
    let retryCount = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const clearReconnect = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = (reason: string) => {
      if (disposed || document.visibilityState !== "visible" || !navigator.onLine) return;
      clearReconnect();
      const delay = Math.min(30_000, 1000 * 2 ** retryCount);
      retryCount += 1;
      console.warn(`[Realtime/Support] ${reason}; reconnecting in ${delay}ms`);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void setup();
      }, delay);
    };

    const setup = async () => {
      if (disposed) return;
      const previous = channel;
      channel = null;
      if (previous) await supabase.removeChannel(previous);
      if (disposed) return;

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;
      supabase.realtime.setAuth(session.access_token);

      channel = supabase
        .channel(`support-${session.user.id}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "support_conversations",
            filter: `customer_id=eq.${session.user.id}`,
          },
          (payload) => {
            const next = payload.new as SupportConversation;
            if (next.id === conversationIdRef.current) {
              setConversation(next);
            }
          },
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "support_messages",
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload) => {
            const msg = payload.new as SupportMessage;
            // Avoid duplicating messages we just optimistically appended.
            setMessages((prev) =>
              prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
            );
          },
        )
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "support_messages",
            filter: `conversation_id=eq.${conversationId}`,
          },
          (payload) => {
            const msg = payload.new as SupportMessage;
            setMessages((prev) =>
              prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)),
            );
          },
        )
        .subscribe((status, err) => {
          if (disposed) return;
          if (status === "SUBSCRIBED") {
            retryCount = 0;
          } else if (status === "CHANNEL_ERROR") {
            scheduleReconnect(`Channel error${err ? `: ${String(err)}` : ""}`);
          } else if (status === "TIMED_OUT") {
            scheduleReconnect("Subscription timed out");
          } else if (status === "CLOSED") {
            scheduleReconnect("Channel closed");
          }
        });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        retryCount = 0;
        clearReconnect();
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          void setup();
        }, 300);
      } else {
        clearReconnect();
      }
    };
    const onOnline = () => {
      retryCount = 0;
      clearReconnect();
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void setup();
      }, 1000);
    };

    void setup();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      disposed = true;
      clearReconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      if (channel) void supabase.removeChannel(channel);
    };
  }, [authed, active, conversation?.id, supabase]);

  // 패널이 **닫혀 있는 동안**의 배지 신선도. 실시간 채널은 열렸을 때만 붙이므로(연결
  // 비용), 닫힌 상태에서 도착한 시스템 메시지(크루 알림 등)는 다음 페이지 로드까지
  // 배지에 안 잡힌다. 대신 탭이 보일 때 60초마다, 그리고 탭이 다시 보이는 순간 대화
  // 행만 가볍게 다시 읽는다(메시지 목록은 안 읽는다). 대화가 새로 생긴 경우(null → id)
  // 에만 전체 refresh 로 메시지까지 채운다 — 열었을 때 빈 목록이 되지 않게.
  useEffect(() => {
    if (!authed || active) return;

    let disposed = false;
    const pollConversation = async () => {
      if (disposed || document.visibilityState !== "visible") return;
      try {
        const { data: next } = await getMyActiveConversation();
        if (disposed) return;
        if (next && next.id !== conversationIdRef.current) {
          await refresh();
          return;
        }
        if (next) setConversation(next);
      } catch (error) {
        console.warn("[Support] conversation poll failed:", error);
      }
    };

    const timer = window.setInterval(() => void pollConversation(), 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void pollConversation();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [authed, active, refresh]);

  // Mark as read when active and there are unread admin messages.
  // Fire-and-forget Server Action: catch the rejection here so a network/lifecycle
  // abort doesn't surface as an unhandled "Failed to fetch" in Sentry.
  const markAsRead = useCallback(() => {
    if (!conversation) return;
    if (conversation.customer_unread_count <= 0) return;
    const conversationId = conversation.id;
    markSupportConversationAsRead(conversationId)
      .then((result) => {
        // 배지는 즉시 내린다 — 실시간 UPDATE 이벤트가 늦거나 유실돼도 "읽었는데
        // 배지가 남는" 상태를 만들지 않는다. 서버 카운트는 액션이 이미 0 으로 맞췄다.
        if (!result.success) return;
        setConversation((prev) =>
          prev && prev.id === conversationId
            ? { ...prev, customer_unread_count: 0 }
            : prev,
        );
      })
      .catch((error) => {
        console.warn("[Support] markAsRead failed:", error);
      });
  }, [conversation]);

  // Auto mark-as-read when panel opens with unread messages
  useEffect(() => {
    if (active) markAsRead();
  }, [active, markAsRead]);

  const appendMessage = useCallback((msg: SupportMessage) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  return {
    conversation,
    messages,
    loading,
    unreadCount: conversation?.customer_unread_count ?? 0,
    markAsRead,
    refresh,
    setConversation,
    appendMessage,
  };
}
